<template>
  <div class="floor-bid-pad">
    <div class="floor-bid-pad__head">
      <div class="floor-bid-pad__head-row">
        <span class="floor-bid-pad__label">Bid amount</span>
        <span class="floor-bid-pad__eyebrow">Floor bid pad</span>
      </div>

      <form class="floor-bid-pad__form" @submit.prevent="submit">
        <div class="floor-bid-pad__field">
          <span class="floor-bid-pad__currency">$</span>
          <InputText
            ref="amountField"
            class="floor-bid-pad__input"
            type="text"
            :model-value="typedAmount"
            :disabled="disabled"
            placeholder="Enter to type new…"
            aria-label="Bid amount"
            @input="onInput"
            @focus="(event) => event.target.select()"
          />
        </div>

        <Button
          class="floor-bid-pad__submit"
          :class="{ 'floor-bid-pad__submit--ready': ready }"
          type="submit"
          :disabled="!ready"
          title="Set the current bid to this amount"
        >
          <span>Set bid</span>
          <span class="floor-bid-pad__shortcut">SPACE</span>
        </Button>
      </form>
    </div>

    <div class="floor-bid-pad__grid">
      <BidPadCell
        v-for="cell in cells"
        :key="cell.key"
        :price="cell.price"
        :tag="cell.tag"
        :tone="phase === 'bidding' ? 'primary' : 'info'"
        :disabled="disabled"
        @bid="(price) => emit('bid', price)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import BidPadCell from '../BidPadCell/BidPadCell.vue';
import { buildBidPad } from '../../utils/bidPad.js';

// The bid-entry keypad: a typed amount over a grid of one-tap prices.
//
// It called the runtime directly — and called a DIFFERENT route depending on
// the lot's phase, because tapping a price while the auctioneer is still asking
// moves the ask rather than recording a bid. Both are one `bid` event now; the
// application knows the phase it passed in and routes accordingly.
//
// The grid itself is still built here, from `utils/bidPad.js`. Laying out a
// ladder of prices is presentation, and the alternative — the application
// passing 14 cells in — would put the pad's whole design outside the pad.
defineOptions({ name: 'FloorBidPad' });

const props = defineProps({
  // The standing high bid, or null while the auctioneer is still asking.
  currentBid: { type: Number, default: null },
  // What the auctioneer is asking now. The pad is built around it.
  askingBid: { type: Number, default: 0 },
  // The lot's bid increment; the fine ladder steps by it.
  increment: { type: Number, default: null },
  // bidding | asking. Tints the pad, and tells the application how to read a
  // `bid` — a price tapped while asking is a new ask, not a bid.
  phase: { type: String, default: 'bidding', validator: (value) => ['bidding', 'asking'].includes(value) },
  // The lot is finalized, or a bid is in flight.
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['bid']);

const pad = computed(() =>
  buildBidPad({ currentBid: props.currentBid, askingBid: props.askingBid, increment: props.increment })
);

// Flattened with a stable key, since the grid flows the rows itself.
const cells = computed(() =>
  pad.value.rows.flatMap((row, rowIndex) =>
    row.map((cell, columnIndex) => ({ ...cell, key: `${rowIndex}-${columnIndex}` }))
  )
);

// ── The typed amount ────────────────────────────────────────────────────────

const typedAmount = ref(String(props.askingBid || ''));

// Re-prefill as the ask moves, so the box always opens on the number the clerk
// would most likely type.
watch(
  () => props.askingBid,
  (asking) => {
    typedAmount.value = String(asking || '');
  }
);

const onInput = (event) => {
  typedAmount.value = event.target.value.replace(/[^0-9.,]/g, '');
};

const parsedAmount = computed(() => Number(String(typedAmount.value).replace(/,/g, '')));
const ready = computed(() => !props.disabled && typedAmount.value !== '' && parsedAmount.value > 0);

function submit() {
  if (ready.value) emit('bid', parsedAmount.value);
}

// ── Enter jumps to the box ──────────────────────────────────────────────────
//
// A cockpit shortcut, and the reason it is here rather than in the application:
// it is about THIS field's focus, and the guard below is the part that has to
// be right — Enter must not be stolen while the clerk is typing in the run
// search, the halt reason, or the winning badge.

const amountField = ref(null);

const onKeydown = (event) => {
  if (event.key !== 'Enter') return;

  const focused = document.activeElement;
  if (focused === amountField.value?.$el) return;
  if (focused?.isContentEditable) return;
  if (focused && ['INPUT', 'TEXTAREA', 'SELECT'].includes(focused.tagName)) return;

  event.preventDefault();
  typedAmount.value = '';
  requestAnimationFrame(() => amountField.value?.$el?.focus());
};

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>

<style scoped>
.floor-bid-pad {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--p-floor-bid-pad-background);
  border: var(--p-floor-bid-pad-border-width) solid var(--p-floor-bid-pad-border-color);
  border-radius: var(--p-floor-bid-pad-border-radius);
}

.floor-bid-pad__head {
  display: flex;
  flex-direction: column;
  padding: var(--p-floor-bid-pad-head-padding);
  border-bottom: var(--p-floor-bid-pad-head-border-bottom-width) solid
    var(--p-floor-bid-pad-head-border-bottom-color);
  background: var(--p-floor-bid-pad-head-background);
  gap: var(--p-floor-bid-pad-head-gap);
}

.floor-bid-pad__head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.floor-bid-pad__label {
  text-transform: uppercase;
  font-size: var(--p-floor-bid-pad-label-font-size);
  font-weight: var(--p-floor-bid-pad-label-font-weight);
  letter-spacing: var(--p-floor-bid-pad-label-letter-spacing);
  color: var(--p-floor-bid-pad-label-color);
}

.floor-bid-pad__eyebrow {
  text-transform: uppercase;
  font-size: var(--p-floor-bid-pad-eyebrow-font-size);
  font-weight: var(--p-floor-bid-pad-eyebrow-font-weight);
  letter-spacing: var(--p-floor-bid-pad-eyebrow-letter-spacing);
  color: var(--p-floor-bid-pad-eyebrow-color);
}

.floor-bid-pad__form {
  display: flex;
  align-items: stretch;
  gap: var(--p-floor-bid-pad-form-gap);
}

.floor-bid-pad__field {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--p-floor-bid-pad-field-gap);
  padding: var(--p-floor-bid-pad-field-padding);
  border: var(--p-floor-bid-pad-field-border-width) solid var(--p-floor-bid-pad-field-border-color);
  border-radius: var(--p-floor-bid-pad-field-border-radius);
  background: var(--p-floor-bid-pad-field-background);
}

.floor-bid-pad__currency {
  line-height: 1;
  font-size: var(--p-floor-bid-pad-currency-font-size);
  font-weight: var(--p-floor-bid-pad-currency-font-weight);
  color: var(--p-floor-bid-pad-currency-color);
}

/* InputText draws its own box; strip it back so the field wrapper above is the
   single visible box. */
.p-inputtext.floor-bid-pad__input,
.p-inputtext.floor-bid-pad__input:enabled:focus {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  font-variant-numeric: tabular-nums;
  font-family: var(--p-floor-bid-pad-input-font-family);
  font-size: var(--p-floor-bid-pad-input-font-size);
  font-weight: var(--p-floor-bid-pad-input-font-weight);
  letter-spacing: var(--p-floor-bid-pad-input-letter-spacing);
  line-height: var(--p-floor-bid-pad-input-line-height);
  color: var(--p-floor-bid-pad-input-color);
  padding: var(--p-floor-bid-pad-input-padding);
}

.p-button.floor-bid-pad__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-transform: uppercase;
  font-family: inherit;
  cursor: not-allowed;
  padding: var(--p-floor-bid-pad-submit-padding);
  border-radius: var(--p-floor-bid-pad-submit-border-radius);
  font-size: var(--p-floor-bid-pad-submit-font-size);
  font-weight: var(--p-floor-bid-pad-submit-font-weight);
  letter-spacing: var(--p-floor-bid-pad-submit-letter-spacing);
  gap: var(--p-floor-bid-pad-submit-gap);
  background: var(--p-floor-bid-pad-submit-background);
  color: var(--p-floor-bid-pad-submit-color);
  border: var(--p-floor-bid-pad-submit-border-width) solid var(--p-floor-bid-pad-submit-border-color);
}

.p-button.floor-bid-pad__submit--ready {
  cursor: pointer;
  background: var(--p-floor-bid-pad-submit-ready-background);
  color: var(--p-floor-bid-pad-submit-ready-color);
  border-color: var(--p-floor-bid-pad-submit-ready-border-color);
  box-shadow: var(--p-floor-bid-pad-submit-ready-box-shadow);
}

.floor-bid-pad__shortcut {
  opacity: 0.7;
  text-transform: none;
  letter-spacing: 0;
  font-family: var(--p-floor-bid-pad-shortcut-font-family);
  font-size: var(--p-floor-bid-pad-shortcut-font-size);
  font-weight: var(--p-floor-bid-pad-shortcut-font-weight);
  padding: var(--p-floor-bid-pad-shortcut-padding);
  border-radius: var(--p-floor-bid-pad-shortcut-border-radius);
  background: var(--p-floor-bid-pad-shortcut-background);
}

.floor-bid-pad__submit--ready .floor-bid-pad__shortcut {
  background: var(--p-floor-bid-pad-submit-ready-shortcut-background);
}

.floor-bid-pad__grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  padding: var(--p-floor-bid-pad-grid-padding);
  gap: var(--p-floor-bid-pad-grid-gap);
}
</style>
